import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Download, CheckCircle2, XCircle, FileCheck2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ST_COLORS = { pending: "#F59E0B", approved: "#2563EB", rejected: "#EF4444" };

export default function ApprovalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => axios.get(`${API}/public/approve/${token}`)
    .then((r) => setData(r.data))
    .catch((e) => setErr(e.response?.data?.detail || "This link is invalid or was removed"));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (decision) => {
    setBusy(decision);
    try {
      await axios.post(`${API}/public/approve/${token}`, { decision, comment });
      toast.success(decision === "approved" ? "Approved — thank you!" : "Rejection recorded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record your decision");
    } finally {
      setBusy("");
    }
  };

  const fileUrl = `${API}/public/approve/${token}/file`;

  if (err) {
    return <div className="min-h-screen flex items-center justify-center p-6"><p className="text-muted-foreground" data-testid="approve-error">{err}</p></div>;
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const isImage = (data.content_type || "").startsWith("image/");
  const isPdf = (data.content_type || "").includes("pdf");
  const acted = data.you.status !== "pending";
  const resolved = data.status !== "pending";

  return (
    <div className="min-h-screen bg-secondary/30 py-8 px-4" data-testid="approval-page">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="bg-white border border-border rounded-xl p-6">
          <p className="overline text-muted-foreground text-[11px] uppercase tracking-[0.2em] mb-1">Approval requested · {data.project_name}</p>
          <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2.5">
            <FileCheck2 className="h-6 w-6 text-primary shrink-0" /> {data.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            {data.display_id} · {data.doc_type} · v{data.version} · requested by {data.requested_by_name}{data.due_date ? ` · needed by ${data.due_date}` : ""}
          </p>
          {data.note && <p className="text-sm mt-3 border-l-2 border-primary/30 pl-3" data-testid="approve-note">{data.note}</p>}
          <div className="flex flex-wrap gap-2 mt-4">
            {data.approvers.map((ap, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 text-xs border border-border rounded-full px-2.5 py-1 bg-secondary/40">
                <span className="h-2 w-2 rounded-full" style={{ background: ST_COLORS[ap.status] }} /> {ap.name} · {ap.status}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold">{data.filename} <span className="text-muted-foreground font-normal">({Math.round((data.size || 0) / 1024)} KB)</span></p>
            <a href={`${fileUrl}?download=1`} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="approve-download">
              <Download className="h-4 w-4" /> Download
            </a>
          </div>
          {isImage ? (
            <img src={fileUrl} alt={data.filename} className="max-h-[70vh] mx-auto rounded border border-border" data-testid="approve-preview-image" />
          ) : isPdf ? (
            <iframe src={fileUrl} title="Document preview" className="w-full h-[70vh] rounded border border-border" data-testid="approve-preview-pdf" />
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">No inline preview for this file type — use Download to review it.</p>
          )}
        </div>

        <div className="bg-white border border-border rounded-xl p-6" data-testid="approve-decision-box">
          {acted ? (
            <div className="text-center py-2">
              {data.you.status === "approved" ? <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" /> : <XCircle className="h-10 w-10 text-red-500 mx-auto mb-2" />}
              <p className="font-semibold" data-testid="approve-acted">You {data.you.status} this document{data.you.comment ? ` — "${data.you.comment}"` : ""}.</p>
              <p className="text-sm text-muted-foreground mt-1">
                Overall status: <b style={{ color: ST_COLORS[data.status] }}>{data.status}</b>
                {data.status === "approved" ? " · filed in the project Library under Approved Docs." : data.status === "rejected" ? " · the requester will send a revised version." : " · waiting on other approvers."}
              </p>
            </div>
          ) : resolved ? (
            <p className="text-center text-sm text-muted-foreground py-2" data-testid="approve-resolved">
              This request was already <b style={{ color: ST_COLORS[data.status] }}>{data.status}</b> — no action needed.
            </p>
          ) : (
            <>
              <p className="text-sm font-semibold mb-2">Your decision, {data.you.name || data.you.email}</p>
              <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value.slice(0, 1000))} placeholder="Optional comment (required context if rejecting)…" data-testid="approve-comment" />
              <div className="flex gap-3 mt-4">
                <Button className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => decide("approved")} disabled={!!busy} data-testid="approve-button">
                  {busy === "approved" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Approve
                </Button>
                <Button variant="outline" className="flex-1 gap-2 text-red-600 border-red-200 hover:bg-red-50" onClick={() => decide("rejected")} disabled={!!busy} data-testid="reject-button">
                  {busy === "rejected" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />} Reject
                </Button>
              </div>
            </>
          )}
        </div>
        <p className="text-center text-[11px] uppercase tracking-widest text-muted-foreground">PM Orchestrator · secure approval link</p>
      </div>
    </div>
  );
}
